import type {} from 'pinia-plugin-synced'

import { useLocalStorageManualReset } from '@proj-airi/stage-shared/composables'
import { refManualReset } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, watch } from 'vue'

import { useProviderStore } from '../providers/provider'
import { useConsciousnessSettingsStore } from './consciousness-settings'

export const useConsciousnessStore = defineStore('consciousness', () => {
  const providersStore = useProviderStore()
  const settingsStore = useConsciousnessSettingsStore()

  // Pinia synchronization owns live cross-window state. localStorage remains
  // durable persistence, but storage events must not reflect state back into
  // the store and publish another synchronized snapshot.
  const persistenceOptions = { listenToStorageChanges: false }

  // State
  const activeProvider = useLocalStorageManualReset<string>('settings/consciousness/active-provider', '', persistenceOptions)
  const activeModel = useLocalStorageManualReset<string>('settings/consciousness/active-model', '', persistenceOptions)
  const activeCustomModelName = useLocalStorageManualReset<string>('settings/consciousness/active-custom-model', '', persistenceOptions)
  const expandedDescriptions = refManualReset<Record<string, boolean>>(() => ({}))
  const modelSearchQuery = refManualReset<string>('')

  // Computed properties
  const supportsModelListing = computed(() => {
    return providersStore.supportsModelListing(activeProvider.value)
  })

  const providerModels = computed(() => {
    return providersStore.getModelsForProvider(activeProvider.value)
  })

  function isOpenRouterProvider(provider: string) {
    return providersStore.findProviderDefinition(provider)?.id === 'openrouter-ai'
  }

  const activeModelRequiresReasoning = computed(() => {
    return isOpenRouterProvider(activeProvider.value)
      && providerModels.value.find(model => model.id === activeModel.value)?.reasoning?.mandatory === true
  })

  const activeModelUsesProviderReasoningDefault = computed(() => {
    return isOpenRouterProvider(activeProvider.value)
      && !providerModels.value.some(model => model.id === activeModel.value)
  })

  const isLoadingActiveProviderModels = computed(() => {
    return providersStore.isLoadingModels[activeProvider.value] || false
  })

  const activeProviderModelError = computed(() => {
    return providersStore.modelLoadError[activeProvider.value] || null
  })

  const temperature = useLocalStorageManualReset<number>(
    'settings/consciousness/active-temperature',
    0.7,
    persistenceOptions,
  )

  const topP = useLocalStorageManualReset<number>(
    'settings/consciousness/active-top-p',
    1.0,
    persistenceOptions,
  )

  // Saved slider values do not imply consent to override provider defaults.
  // All request paths consume these projections; disabled fields stay undefined.
  const activeTemperature = computed(() => settingsStore.temperatureEnabled ? temperature.value : undefined)
  const activeTopP = computed(() => settingsStore.topPEnabled ? topP.value : undefined)

  const filteredModels = computed(() => {
    if (!modelSearchQuery.value.trim()) {
      return providerModels.value
    }

    const query = modelSearchQuery.value.toLowerCase().trim()
    return providerModels.value.filter(model =>
      model.name.toLowerCase().includes(query)
      || model.id.toLowerCase().includes(query)
      || (model.description && model.description.toLowerCase().includes(query)),
    )
  })

  function resetModelSelection() {
    activeModel.reset()
    activeCustomModelName.reset()
    expandedDescriptions.reset()
    modelSearchQuery.reset()
  }

  // A model id belongs to the catalog of the provider it was picked from, so
  // clear the selection whenever the provider changes. This used to live only
  // in the consciousness settings page, so provider changes made elsewhere
  // (onboarding, character cards, provider deletion) kept the previous
  // provider's model and chat requests failed upstream with model_not_found.
  //
  // The watcher is synchronous on purpose: call sites assign the provider
  // first and a new model right after, so a
  // deferred reset would wipe the model they just chose. Synchronous flush
  // makes "set provider, then set model" a safe, ordered operation.
  //
  // Issue #1761: https://github.com/moeru-ai/airi/issues/1761
  watch(activeProvider, (provider, oldProvider) => {
    if (provider === oldProvider)
      return

    activeModel.value = ''
    activeCustomModelName.value = ''
  }, { flush: 'sync' })

  async function loadModelsForProvider(provider: string) {
    if (providersStore.supportsModelListing(provider)) {
      await providersStore.fetchModelsForProvider(provider)
    }
  }

  async function getModelsForProvider(provider: string) {
    if (providersStore.supportsModelListing(provider)) {
      return providersStore.getModelsForProvider(provider)
    }

    return []
  }

  /** Resolves a provider with the reasoning mode shared by every Consciousness input path. */
  async function getChatProviderInstance(provider: string) {
    const selectedModel = providerModels.value.find(model => model.id === activeModel.value)
    const useProviderReasoningDefault = provider === activeProvider.value
      && isOpenRouterProvider(provider)
      && (activeModelUsesProviderReasoningDefault.value || selectedModel?.reasoning?.mandatory === true)

    // OpenRouter applies the model default when reasoning is mandatory or unknown.
    if (useProviderReasoningDefault) {
      return providersStore.getChatProviderInstance(provider)
    }

    return providersStore.getChatProviderInstance(provider, {
      reasoning: settingsStore.reasoning ? 'enabled' : 'disabled',
    })
  }

  const configured = computed(() => {
    return !!activeProvider.value && !!activeModel.value
  })

  /** Whether chat can send. The `prompt-api` provider runs without a selected model. */
  const chatReady = computed(() => {
    return !!activeProvider.value && (!!activeModel.value || activeProvider.value === 'prompt-api')
  })

  async function resetState() {
    activeProvider.reset()
    resetModelSelection()
    temperature.reset()
    topP.reset()
    await settingsStore.setTemperatureEnabled(false)
    await settingsStore.setTopPEnabled(false)
  }

  return {
    // State
    configured,
    chatReady,
    activeProvider,
    activeModel,
    activeTemperature,
    activeTopP,
    temperature,
    topP,
    customModelName: activeCustomModelName,
    expandedDescriptions,
    modelSearchQuery,

    // Computed
    supportsModelListing,
    providerModels,
    activeModelRequiresReasoning,
    activeModelUsesProviderReasoningDefault,
    isLoadingActiveProviderModels,
    activeProviderModelError,
    filteredModels,

    // Actions
    resetModelSelection,
    loadModelsForProvider,
    getModelsForProvider,
    getChatProviderInstance,
    resetState,
  }
}, {
  synced: {
    state: true,
  },
})
